'use client';

import { useSearchParams } from 'next/navigation';
import { getCategoryLabel } from '@/utils/sanity/sanity';
import Link from 'next/link';
import { analytics } from '@/lib/analytics';

export const CategoryButton = ({
  category,
  isSelected,
  href,
}: {
  category: string | null;
  isSelected: boolean;
  href: string;
}) => {
  const onClick = () => {
    analytics.capture('blog.category_filter.clicked', { category });
  };
  return (
    <Link
      href={href}
      className={`px-4 py-2 rounded-full text-base font-medium transition-all duration-200 border ${isSelected ? 'border-white text-white' : 'border-zinc-700 text-white hover:border-white'}`}
      onClick={onClick}
    >
      {category ? getCategoryLabel(category) : 'All'}
    </Link>
  );
};

interface BlogCategoryFilterProps {
  availableCategories: string[];
  selectedCategory?: string;
}

export default function BlogCategoryFilter({
  availableCategories,
  selectedCategory,
}: BlogCategoryFilterProps) {
  const searchParams = useSearchParams();

  const buildUrl = (category: string | null) => {
    const params = new URLSearchParams(searchParams);

    if (category) {
      params.set('category', category);
    } else {
      params.delete('category');
    }

    // Reset to page 1 when changing category
    params.delete('page');

    const queryString = params.toString();
    return queryString ? `/blog?${queryString}` : '/blog';
  };

  if (availableCategories.length === 0) {
    return null;
  }

  const onClick = () => {
    analytics.capture('blog.category_filter.clicked', { category: null });
  };

  return (
    <div className="space-y-4 flex justify-center">
      <div className="flex flex-wrap gap-3 justify-center">
        {/* All Posts Button */}
        <CategoryButton href={buildUrl(null)} isSelected={!selectedCategory} category={null} />

        {/* Category Buttons */}
        {availableCategories.map((category) => {
          const isSelected = selectedCategory === category;

          return (
            <CategoryButton
              key={category}
              href={buildUrl(category)}
              isSelected={isSelected}
              category={category}
            />
          );
        })}
      </div>
    </div>
  );
}
